/**
 * #1560 — a model turn that fails is shown to the owner, never swallowed.
 *
 * One table for every TypeScript client (the OpenClaw entry and the room
 * bridges): the machine codes a failed turn can carry, the one plain sentence
 * the owner reads for each, a matcher that recognises those sentences (so a
 * bridge never treats its own notice as a reply it owes), and the helper that
 * records the failure as a `model_turn` span for the agent's "Recent problems".
 *
 * Every code here is also in `KNOWN_TOOL_ERROR_CODES` and in
 * `docs/contracts/agent-tool-error-codes.json`, and none is a send code — the
 * turn failed before anything was sent. `turn-failure-1560.test.ts` pins that.
 */

import { reportToolInvoke, type ToolConversation, type ToolSpanSink } from "./tool-telemetry.js";

export const TURN_FAILURE_CODES = [
  "provider_auth_failed",
  "provider_billing",
  "model_rate_limited",
  "model_unavailable",
  "provider_unreachable",
  "ai_tool_unsupported",
  "plan_limit_reached",
  "model_error",
] as const;
export type TurnFailureCode = (typeof TURN_FAILURE_CODES)[number];
const CODES: ReadonlySet<string> = new Set(TURN_FAILURE_CODES);

function asTurnFailureCode(code: unknown): TurnFailureCode {
  return typeof code === "string" && CODES.has(code) ? (code as TurnFailureCode) : "model_error";
}

/**
 * A failed model turn with its machine code. The message stays generic
 * (default "Codex turn failed") so a log line or a thrown error never carries
 * provider output; the code is what callers branch on.
 */
export class TurnFailure extends Error {
  readonly code: TurnFailureCode;
  readonly exitCode?: number;

  constructor(code: TurnFailureCode, opts: { exitCode?: number } = {}, message = "Codex turn failed") {
    super(message);
    this.name = "TurnFailure";
    this.code = asTurnFailureCode(code);
    if (opts.exitCode !== undefined) this.exitCode = opts.exitCode;
  }
}

/** One line, no '@' (so it can never address anyone — #1107), bounded length. */
function clean(value: unknown, max: number): string {
  if (typeof value !== "string") return "";
  return value.replace(/@/g, "").replace(/\s+/g, " ").trim().slice(0, max).trim();
}

/**
 * The sentence the owner reads when a turn fails with `code`. No link, no
 * emoji, no '@'. An unknown code gets the bare `model_error` line.
 *
 * ROOMS: a caller posting into a room must pass `"model_error"`. The coded
 * sentences describe the OWNER's own provider account (sign-in, billing,
 * plan), which is not something to tell everyone else in the room.
 */
export function ownerNoticeFor(
  code: TurnFailureCode,
  ctx: { agentName: string; provider: string },
): string {
  const a = clean(ctx?.agentName, 80) || "Your agent";
  const p = clean(ctx?.provider, 40) || "AI provider";
  const head = `${a} couldn't answer this message`;
  switch (asTurnFailureCode(code)) {
    case "provider_auth_failed":
      return `${head}: its ${p} sign-in expired. Sign in again on the agent's computer.`;
    case "provider_billing":
      return `${head}: its ${p} account is out of credit or has a billing problem.`;
    case "model_rate_limited":
      return `${head}: it is being rate-limited by ${p}. Try again in a few minutes.`;
    case "model_unavailable":
      return `${head}: the model it is set to use is no longer available.`;
    case "provider_unreachable":
      return `${head}: it can't reach ${p} right now.`;
    case "ai_tool_unsupported":
      return `${head}: its AI tool needs attention on its computer.`;
    case "plan_limit_reached":
      return `${head}: it has reached its ${p} plan's usage limit.`;
    default:
      return `${head}.`;
  }
}

const NOTICE_RE = /^[^\n@]{1,120} couldn't answer this message(?:\.|: [^\n]{1,200})$/;

/** True only for a single line shaped like an `ownerNoticeFor` sentence. */
export function isOwnerNotice(text: unknown): boolean {
  return typeof text === "string" && NOTICE_RE.test(text);
}

export interface ModelTurnFailureReport {
  code: TurnFailureCode;
  conversation: ToolConversation;
  durationMs: number;
  /** `plan_limit_reached` only: whole epoch seconds the plan resets (#1467). */
  limitResetsAt?: number;
}

/**
 * Record one failed turn as a `model_turn` error span. A plan-limit span names
 * no conversation (#1467: the plan is the owner's, not the room's) and is the
 * only one that carries the reset time. Never throws.
 */
export function reportModelTurnFailure(
  sink: ToolSpanSink | null | undefined,
  report: ModelTurnFailureReport,
): void {
  try {
    const code = asTurnFailureCode(report.code);
    const isPlan = code === "plan_limit_reached";
    reportToolInvoke(sink, {
      toolName: "model_turn",
      outcome: "error",
      errorKind: code,
      durationMs: report.durationMs,
      conversation: isPlan ? { kind: "unknown" } : report.conversation,
      ...(isPlan && report.limitResetsAt !== undefined ? { limitResetsAt: report.limitResetsAt } : {}),
    });
  } catch {
    // telemetry must never break a turn
  }
}
